import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import AuthLeftBranding from "../AuthComponent/SignInProcess/AuthLeftBranding";
import PlanCardPromoBadges from "./PlanCardPromoBadges";
import { getApi } from "../../Services/apiService";
import { toast } from "react-toastify";
import { hasAuthToken } from "../../Utils/authStorage";
import { ensurePlanLabel, getSubscriptionPlansApiUrl } from "../../Utils/subscriptionAccess";
import "../../Auth.css";

/** Matches DB comment: 1:monthly, 2:quarterly, 3:yearly, 4:lifetime */
const BILLING_CYCLE_LABELS = {
  1: "Monthly",
  2: "3-Month",
  3: "Yearly",
  4: "Lifetime",
};

/** Matches API: unified `lifetime` array (plan_type 4 mother promo, 7 father promo) + legacy buckets */
const PLAN_TYPE_LABELS = {
  1: "Mother's Side",
  2: "Father's Side",
  3: "Both Sides",
  4: "Lifetime (Mother's side)",
  7: "Lifetime (Father's side)",
};

const PLAN_SECTIONS = [
  { key: "mothers_side", title: PLAN_TYPE_LABELS[1], group: "recurring" },
  { key: "fathers_side", title: PLAN_TYPE_LABELS[2], group: "recurring" },
  { key: "both_sides", title: "", group: "recurring", hideTitle: true },
  { key: "lifetime", title: "", group: "lifetime", hideTitle: true },
  { key: "lifetime_mother_promo", title: PLAN_TYPE_LABELS[4], group: "lifetime" },
  { key: "lifetime_father_promo", title: PLAN_TYPE_LABELS[7], group: "lifetime" },
  { key: "lifetime_promo", title: "", group: "lifetime", hideTitle: true },
];

const PLAN_GROUPS = [
  { key: "recurring", title: "Recurring Plans" },
  { key: "lifetime", title: "Lifetime Plans" },
];

function billingCycleLabel(plan) {
  const raw = plan?.billing_cycle;
  if (raw === undefined || raw === null || raw === "") return "";
  const key = Number(String(raw).trim());
  if (Number.isFinite(key) && BILLING_CYCLE_LABELS[key]) {
    return BILLING_CYCLE_LABELS[key];
  }
  return "";
}

function isYearlyPlan(plan) {
  const raw = plan?.billing_cycle;
  if (raw === undefined || raw === null || raw === "") return false;
  return Number(String(raw).trim()) === 3;
}

function planTypeLabel(planType) {
  if (planType === undefined || planType === null || planType === "") return "";
  const key = Number(planType);
  if (Number.isFinite(key) && PLAN_TYPE_LABELS[key]) {
    return PLAN_TYPE_LABELS[key];
  }
  return "";
}

function activePlanTypesFromPayload(inner) {
  if (!inner || typeof inner !== "object") return [];
  const direct = Array.isArray(inner.active_plan_types) ? inner.active_plan_types : [];
  if (direct.length > 0) return direct;
  const subs = Array.isArray(inner.active_subscriptions) ? inner.active_subscriptions : [];
  if (subs.length === 0) return [];
  const types = new Set();
  subs.forEach((s) => {
    if (s?.plan_type != null) types.add(s.plan_type);
  });
  return [...types];
}

function normalizePlansResponse(res) {
  if (!res || typeof res !== "object") {
    return { active_plan_types: [], plans: {} };
  }
  if (res.plans && typeof res.plans === "object") {
    return {
      active_plan_types: activePlanTypesFromPayload(res),
      plans: res.plans,
    };
  }
  if (res.data && typeof res.data === "object" && res.data.plans) {
    return {
      active_plan_types: activePlanTypesFromPayload(res.data),
      plans: res.data.plans,
    };
  }
  return { active_plan_types: [], plans: {} };
}

function formatMoney(value, currency = "USD") {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  try {
    return new Intl.NumberFormat("en-US", { style: "currency", currency }).format(n);
  } catch {
    return `$${n.toFixed(2)}`;
  }
}

function formatPlanDisplayName(plan) {
  const raw = (plan?.name && String(plan.name).trim()) || "";
  if (!raw) return "";
  const spaced = raw.replace(/_/g, " ");
  const titled = spaced.replace(/\b\w/g, (c) => c.toUpperCase());
  // Title-case treats ’ or ' as a word break, producing Mother’S; normalize possessive ’s / 's.
  return titled.replace(/(['\u2019])S(?=\s|$)/g, "$1s");
}

/** Lifetime both-sides card (plan_type 8 or mother+father naming). */
function isMotherFatherSidesPromoPlan(plan) {
  if (Number(plan?.plan_type) === 8) return true;
  const slug = String(plan?.name ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_");
  if (
    slug.includes("mother_father_sides") &&
    (slug.includes("promo") || slug.includes("_plan") || slug.endsWith("plan"))
  ) {
    return true;
  }
  const display = formatPlanDisplayName(plan).toLowerCase();
  const hasSidesBundle =
    display.includes("mother") &&
    display.includes("father") &&
    display.includes("sides") &&
    (display.includes("promo") || display.includes("plan"));
  return hasSidesBundle;
}

function lifetimeCardTitle(plan) {
  if (isMotherFatherSidesPromoPlan(plan)) return "Both Sides";
  const planType = Number(plan?.plan_type);
  if (planType === 4) return "Mother's Side";
  if (planType === 7) return "Father's Side";
  const display = formatPlanDisplayName(plan).toLowerCase();
  if (display.includes("mother") && !display.includes("father")) return "Mother's Side";
  if (display.includes("father") && !display.includes("mother")) return "Father's Side";
  return String(formatPlanDisplayName(plan) || plan.name || "Plan")
    .replace(/\bPromo\b/gi, "")
    .replace(/\bPlan\b/gi, "")
    .replace(/\s+/g, " ")
    .trim();
}

/** Prefer promo_amount when promo is active; else amount / price from API. */
function getPlanDisplayAmount(plan) {
  if (plan?.is_promo_active && plan?.promo_amount != null && plan.promo_amount !== "") {
    return plan.promo_amount;
  }
  if (plan?.amount != null && plan.amount !== "") return plan.amount;
  if (plan?.price != null && plan.price !== "") return plan.price;
  return null;
}

function PlanCard({ plan, sectionKey, onSelect }) {
  const displayAmount = getPlanDisplayAmount(plan);
  const currency = (plan.currency || "USD").toString().toUpperCase();
  const cycleLabel = billingCycleLabel(plan);
  const isLifetimeSection =
    sectionKey === "lifetime" ||
    sectionKey === "lifetime_mother_promo" ||
    sectionKey === "lifetime_father_promo" ||
    sectionKey === "lifetime_promo";
  const isBothSidesSection = sectionKey === "both_sides";

  let titleText;
  if (isBothSidesSection) {
    titleText = "Both Sides Yearly Plan";
  } else if (isLifetimeSection) {
    titleText = lifetimeCardTitle(plan);
  } else {
    titleText = ensurePlanLabel(cycleLabel || plan.name || "Plan");
  }

  const featureEntries =
    plan.features && typeof plan.features === "object" && !Array.isArray(plan.features)
      ? Object.entries(plan.features).filter(([, v]) => v != null && v !== "")
      : [];

  const showBestValue =
    !isLifetimeSection && !isBothSidesSection && isYearlyPlan(plan);
  const showBestSavings = isLifetimeSection && isMotherFatherSidesPromoPlan(plan);
  const showMostPopular = isBothSidesSection;
  const hasCornerBalloon = showBestValue || showBestSavings || showMostPopular;

  return (
    <div
      className={`signup-plan-card-wrap${
        hasCornerBalloon ? " signup-plan-card-wrap--corner-balloon" : ""
      }`}
    >
      {hasCornerBalloon ? (
        <PlanCardPromoBadges
          showBestValue={showBestValue}
          showBestSavings={showBestSavings}
          showMostPopular={showMostPopular}
          placement="corner"
        />
      ) : null}
      <div className="signup-plan-card">
        <h3 className="signup-plan-card-title">
          <span className="signup-plan-card-title-main">{titleText}</span>
          {isBothSidesSection ? (
            <em className="signup-plan-card-title-note signup-plan-card-title-note--both-sides">
              (Mother &amp; Father)
            </em>
          ) : null}
        </h3>
        {plan.description ? <p className="signup-plan-card-desc">{plan.description}</p> : null}
        <div className="signup-plan-card-price">{formatMoney(displayAmount, currency)}</div>
        {featureEntries.length > 0 ? (
          <ul className="signup-plan-card-features">
            {featureEntries.map(([k, v]) => (
              <li key={k}>
                <strong>{k}:</strong> {String(v)}
              </li>
            ))}
          </ul>
        ) : null}
        <button type="button" className="signup-plan-card-btn" onClick={() => onSelect(plan, sectionKey)}>
          Make Payment
        </button>
      </div>
    </div>
  );
}

/** Signup /register/plans only — Settings uses its own layout in SubscriptionManagement.jsx */
export default function SubscriptionPlansPicker({
  signupRole = undefined,
  requireAuthRedirect = false,
  signupRedirectOnMissingAuth = "/register",
}) {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [payload, setPayload] = useState({ active_plan_types: [], plans: {} });

  useEffect(() => {
    if (!requireAuthRedirect || hasAuthToken()) return;
    toast.error("Please register or log in first.");
    navigate(signupRedirectOnMissingAuth, {
      replace: true,
      state: signupRole != null ? { role: signupRole } : undefined,
    });
  }, [navigate, requireAuthRedirect, signupRole, signupRedirectOnMissingAuth]);

  const fetchPlans = useCallback(async () => {
    if (requireAuthRedirect && !hasAuthToken()) return;
    if (!hasAuthToken()) return;
    setLoading(true);
    setError("");
    try {
      const res = await getApi(getSubscriptionPlansApiUrl());
      setPayload(normalizePlansResponse(res));
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || "Could not load plans.";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [requireAuthRedirect]);

  useEffect(() => {
    if (hasAuthToken()) {
      fetchPlans();
    }
  }, [fetchPlans]);

  const sectionsWithPlans = useMemo(() => {
    return PLAN_SECTIONS.map((section) => ({
      ...section,
      items: Array.isArray(payload.plans[section.key]) ? payload.plans[section.key] : [],
    })).filter((s) => s.items.length > 0);
  }, [payload.plans]);

  const planGroups = useMemo(() => {
    return PLAN_GROUPS.map((group) => ({
      ...group,
      sections: sectionsWithPlans.filter((s) => s.group === group.key),
    })).filter((g) => g.sections.length > 0);
  }, [sectionsWithPlans]);

  const totalCards = useMemo(
    () => sectionsWithPlans.reduce((n, s) => n + s.items.length, 0),
    [sectionsWithPlans]
  );

  const handleSelectPlan = (plan, sectionKey) => {
    if (!plan) return;
    navigate("/register/payment", {
      state: {
        selectedPlan: plan,
        ...(sectionKey && { planCategory: sectionKey }),
        ...(signupRole != null && { role: signupRole }),
      },
    });
  };

  if (requireAuthRedirect && !hasAuthToken()) {
    return null;
  }

  if (!hasAuthToken()) {
    return null;
  }

  const inner = (
    <div className="signup-plans-inner">
      <div className="signup-plans-header">
        <h1 className="signup-plans-title">Recurring or Lifetime Subscription Plans</h1>
        <p className="signup-plans-urgency">
          HURRY and SIGNUP to lock in this special INTRODUCTORY rate for a year!
        </p>
        <p className="signup-plans-disclaimer">
          Introductory rates end December 31, 2026
        </p>
        {payload.active_plan_types?.length > 0 ? (
          <p className="signup-plans-active-hint">
            Active plan types on your account:{" "}
            {payload.active_plan_types.map((t) => planTypeLabel(t) || String(t)).join(", ")}
          </p>
        ) : null}
      </div>

      {loading ? (
        <div className="signup-plans-loading">Loading plans…</div>
      ) : error ? (
        <div className="signup-plans-error-block" role="alert">
          <div className="signup-payment-error-banner">{error}</div>
          <p className="signup-plans-error-hint">You must load plans and pick one to continue.</p>
          <button type="button" className="signup-plans-skip-btn" onClick={() => fetchPlans()}>
            Try again
          </button>
        </div>
      ) : totalCards === 0 ? (
        <div className="signup-plans-empty">
          <p>No plans are available right now. You need a plan to continue — try refreshing or go back to Sign Up.</p>
          <button type="button" className="signup-plans-skip-btn" onClick={() => fetchPlans()}>
            Try again
          </button>
        </div>
      ) : (
        <div className="signup-plans-sections">
          {planGroups.map((group) => (
            <div key={group.key} className={`signup-plans-group signup-plans-group--${group.key}`}>
              <h2 className="signup-plans-group-title">{group.title}</h2>
              {group.sections.map((section) => {
                const showSectionTitle = !section.hideTitle && section.title;
                const isBothSides = section.key === "both_sides";
                return (
                  <section
                    key={section.key}
                    className={`signup-plans-section${
                      isBothSides ? " signup-plans-section--both-sides" : ""
                    }${section.group === "lifetime" ? " signup-plans-section--lifetime" : ""}`}
                  >
                    {showSectionTitle ? (
                      <h3 className="signup-plans-section-title">{section.title}</h3>
                    ) : null}
                    <div
                      className={`signup-plans-grid${
                        isBothSides
                          ? " signup-plans-grid--both-sides"
                          : section.items.length === 1
                            ? " signup-plans-grid--single"
                            : ""
                      }`}
                    >
                      {section.items.map((plan) => (
                        <PlanCard
                          key={`${section.key}-${plan.id}`}
                          plan={plan}
                          sectionKey={section.key}
                          onSelect={handleSelectPlan}
                        />
                      ))}
                    </div>
                  </section>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="login-main signup-plans-page">
      <div className="left-div">
        <div className="auth-back-to-home-wrap auth-back-to-home-wrap--blue-panel">
          <Button
            as={Link}
            to="/register"
            state={{
              fromSubscriptionPlans: true,
              ...(signupRole != null ? { role: signupRole } : {}),
            }}
            variant="outline-light"
            className="auth-back-to-home-btn auth-back-to-home-btn--blue-panel"
          >
            <span className="auth-back-to-home-arrow" aria-hidden>
              ←
            </span>
            Back
          </Button>
        </div>
        <AuthLeftBranding />
      </div>
      <div className="login-container signup-plans-panel">{inner}</div>
    </div>
  );
}
